import { FC, useState } from 'react'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { Draft, DraftReview } from 'backoffice/components/Drafts/types/types'
import { useGetDraftReviewsQuery } from 'backoffice/components/Drafts/useDraftsQuery'
import ReviewsModal from 'backoffice/components/Drafts/ReviewsModal'
import { getDateTimeString } from 'lib/helpers/dateHelper'
import { getChainName } from 'lib/helpers/chainHelper'

import { ReactComponent as IconComment } from 'images/icons/annotation.svg'

import styles from './DraftSearchCard.module.scss'

interface DraftSearchCardProps {
  draft: Draft
}

const DraftSearchCard: FC<DraftSearchCardProps> = ({ draft }) => {
  const [showAllReviews, setShowAllReviews] = useState(false)
  const { data: draftWithReviews } = useGetDraftReviewsQuery(draft.id)

  const currentDraft = draftWithReviews || draft
  const reviews = currentDraft.recentReviews || []
  const displayedReviews = reviews.slice(0, 2)

  return (
    <>
      <div className={styles.draftResultCard}>
        <div className={styles.draftRibbon}>Draft</div>

        <div className={styles.imageContainer}>
          <img
            src={draft.data.cropImage?.url || draft.data.image?.url}
            alt={draft.data.title}
            className={styles.image}
          />
        </div>

        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.titleRow}>
              <span className={styles.consoleBadge}>Console Petition</span>
              <span className={styles.draftId}>#{draft.id}</span>
              <Link to={`/backoffice/petitions/draft-reviews/${draft.id}`} className={styles.titleLink}>
                <h3 className={styles.title}>{draft.data.title}</h3>
              </Link>
            </div>
            <div className={styles.statusContainer}>
              <span className={cn(styles.statusBadge, styles[draft.status])}>{draft.status.replace('_', ' ')}</span>
            </div>
          </div>

          <div className={styles.metadata}>
            {draft.issuerEmail && (
              <span>
                <strong>Issuer:</strong> {draft.issuerEmail}
              </span>
            )}
            <span>
              <strong>Created:</strong> {getDateTimeString(new Date(draft.createdAt))}
            </span>
            <span>
              <strong>Event Start:</strong> {getDateTimeString(new Date(draft.data.startDate))}
            </span>
            {draft.dropId && (
              <span>
                <strong>Drop ID:</strong> #{draft.dropId}
              </span>
            )}
          </div>

          <p className={styles.description}>{draft.data.description}</p>

          <div className={styles.infoRow}>
            <div className={styles.infoItem}>
              <strong>Supply:</strong>
              <span>{draft.data.mintingConfiguration.supply}</span>
            </div>
            <div className={styles.infoItem}>
              <strong>Chain:</strong>
              <span>{getChainName(draft.data.mintingConfiguration.blockchainId)}</span>
            </div>
            <div className={styles.infoItem}>
              <strong>Distribution:</strong>
              <span>{draft.data.distribution.type}</span>
            </div>
          </div>

          {/* Inline comments preview */}
          {displayedReviews.length > 0 && (
            <div className={styles.commentsPreview}>
              <h4 className={styles.commentsTitle}>
                <IconComment width={14} height={14} />
                Curation Comments
              </h4>
              <div className={styles.commentsList}>
                {displayedReviews.map((review: DraftReview) => (
                  <div key={review.id} className={styles.commentItem}>
                    <div className={styles.commentHeader}>
                      <span className={styles.reviewerName}>{review.reviewedBy}</span>
                      <span className={styles.commentDate}>{getDateTimeString(new Date(review.reviewedAt))}</span>
                    </div>
                    <p className={styles.commentText}>{review.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.actions}>
            {reviews.length > 0 ? (
              <button className={styles.viewCommentsButton} onClick={() => setShowAllReviews(true)}>
                <IconComment />
                Comments ({reviews.length})
              </button>
            ) : (
              <span className={styles.noComments}>
                <IconComment />
                No Comments
              </span>
            )}
          </div>
        </div>
      </div>

      {showAllReviews && <ReviewsModal draftId={draft.id} onClose={() => setShowAllReviews(false)} />}
    </>
  )
}

export default DraftSearchCard
